import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { TrendingDown, Scale, Wallet } from 'lucide-react';
import type { CostosTotales, BalanceMoneda } from '@/types/costo';

interface TotalizadorCostosProps {
  totales: CostosTotales;
}

const formatearMoneda = (monto: number, moneda: 'ARS' | 'USD'): string => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(monto);
};

interface SeccionMonedaProps {
  moneda: 'ARS' | 'USD';
  datos: BalanceMoneda;
}

const SeccionMoneda: React.FC<SeccionMonedaProps> = ({ moneda, datos }) => {
  const tieneMovimientos = datos.ingresos > 0 || datos.egresos > 0;
  if (!tieneMovimientos) return null;

  const balancePositivo = datos.balance >= 0;
  const totalPresupuesto = datos.ingresos + datos.egresos;
  // Lo que realmente se paga a terceros: es lo que el balance le resta al total.
  const egresosReales = datos.egresosReales ?? datos.egresos;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-0.5 rounded">
          {moneda}
        </span>
      </div>

      {/* Total de presupuesto */}
      <div className="flex flex-wrap items-center justify-between gap-x-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <span>Total de presupuesto</span>
        </div>
        <span className="text-sm font-semibold">
          {formatearMoneda(totalPresupuesto, moneda)}
        </span>
      </div>

      {/* Egresos */}
      <div className="flex flex-wrap items-center justify-between gap-x-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <TrendingDown className="h-4 w-4 text-red-500" />
          <span>Egresos</span>
        </div>
        <span className="text-sm font-semibold text-red-500">
          − {formatearMoneda(egresosReales, moneda)}
        </span>
      </div>

      {/* Balance */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 pt-2 border-t">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Scale className="h-4 w-4" />
          <span>Balance</span>
        </div>
        <span
          className={`text-base font-bold ${
            balancePositivo ? 'text-green-600' : 'text-red-500'
          }`}
        >
          {formatearMoneda(datos.balance, moneda)}
        </span>
      </div>

      {/* Progreso de cobros y pagos */}
      {(datos.ingresos > 0 || datos.egresos > 0) && (
        <div className="pt-3 border-t space-y-2.5">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ejecución</p>
          {datos.ingresos > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Cobrado</span>
                <span className="font-semibold text-green-600">{datos.porcentajeCobrado ?? 0}%</span>
              </div>
              <Progress value={datos.porcentajeCobrado ?? 0} className="h-1.5 [&>div]:bg-green-500" />
            </div>
          )}
          {datos.egresos > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Pagado</span>
                <span className="font-semibold text-blue-600">{datos.porcentajePagado ?? 0}%</span>
              </div>
              <Progress value={datos.porcentajePagado ?? 0} className="h-1.5 [&>div]:bg-blue-500" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const TotalizadorCostos: React.FC<TotalizadorCostosProps> = ({
  totales,
}) => {
  const tieneArs = totales.ars.ingresos > 0 || totales.ars.egresos > 0;
  const tieneUsd = totales.usd.ingresos > 0 || totales.usd.egresos > 0;
  const tieneAlgo = tieneArs || tieneUsd;

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">Balance</CardTitle>
      </CardHeader>
      <CardContent>
        {!tieneAlgo ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Agregá items para ver el balance
          </p>
        ) : (
          <div className="space-y-6">
            <SeccionMoneda moneda="ARS" datos={totales.ars} />
            {tieneArs && tieneUsd && <hr />}
            <SeccionMoneda moneda="USD" datos={totales.usd} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
